<script setup>
import {
  createDescriptionMarkup,
  getResource,
  getServices,
  createReservation,
  updateQueryParams,
  updateReservation, getLoggedStatus, citizenEvent,
} from "@/api/httpApi";
import {computed, onMounted, ref, watchEffect} from "vue";
import { useStore } from "vuex";

const emit = defineEmits(["previous", "next", "goto"]);
const store = useStore();

const resource = ref();
const services = ref();

const modalRef = ref(null);
const isLoggedIn = ref(false);

const isServiceActive = computed(() => {
  return services.value?.length > 0;
});

const doesServiceDetermines = computed(() => {
  return isServiceActive.value && resource.value?.enableSlotDuration;
});


watchEffect(() => {
  (async () => {
    const data = await getLoggedStatus();
    isLoggedIn.value = data?.roles?.includes("cittadino");
  })();
});

watchEffect(async () => {
  if (
    store.state.resourceId &&
    (!resource.value || resource.value.resourceId !== store.state.resourceId)
  ) {
    try {
      resource.value = await getResource(store.state.resourceId);
    } catch (err) {
      console.error(err);
      resource.value = undefined;
    }
  }
  if (store.state.resourceId && !services.value) {
    try {
      services.value = await getServices(store.state.resourceId);
    } catch (err) {
      console.error(err);
      services.value = undefined;
    }
  }
});

function resetReservation() {
  window.sessionStorage.clear();
  window.location.reload();
}

async function onNext() {
  const slotsNumberInService = services.value?.find(
    (service) => service.serviceId === store.state.serviceId
  );

  try {
    const payload = {
      detail: store.state.detail,
      email: store.state.user.email,
      erogationMode:
        resource.value.erogationMode === "mis"
          ? store.state.erogationMode
          : resource.value.erogationMode,
      firstName: store.state.user.firstName,
      fiscalCode: store.state.user.fiscalCode,
      lastName: store.state.user.lastName,
      mobilePhone: store.state.user.mobilePhone,
      phone: store.state.user.phone,
      reservationId: store.state.reservationId,
      reservationSlots: store.state.slots,
      reservationNumber: store.state.numberOfReservations,
      resourceId: store.state.resourceId,
      service: {
        description: "",
        serviceId: store.state.serviceId,
        slotsNumber: slotsNumberInService?.slotsNumber || 0,
        visible: true,
      },
    };

    let updatedPayload;

    if (
      store.state.attributes &&
      Object.keys(store.state.attributes).length > 0
    ) {
      updatedPayload = {
        ...payload,
        attributesValues: store.state.attributes,
      };
    } else {
      updatedPayload = payload;
    }

    if(store.state?.identifyValues?.length > 0) {
      updatedPayload = {
        ...updatedPayload,
        itemsBooking: store.state?.identifyValues
      };
    }

    if (store.state.isEditing) {
      citizenEvent(
        {
          message: "Utente modifiche e conclude la prenotazione",
          stepId: 5,
          stepType: "fine",
          resourceId: store?.state?.resourceId ?? ''
        }
      );

      await updateReservation(updatedPayload, isLoggedIn.value);
    } else {
      citizenEvent(
        {
          message: "Utente conferma e conclude la prenotazione",
          stepId: 5,
          stepType: "fine",
          resourceId: store?.state?.resourceId ?? ''
        }
      );
      await createReservation(updatedPayload, isLoggedIn.value);
    }

    updateQueryParams([
      { key: "reservationId", value: store.state.reservationId },
    ]);
    store.commit("setIsEditing", false);
    emit("next");
  } catch (err) {
    console.error(err);
  }
}

onMounted(() => {
  modalRef.value = new window.bootstrap.Modal(
    document.getElementById("editResourceModal")
  );
});
</script>

<template>
  <div class="container p-4">
    <div class="bg-light p-4 shadow-sm">
      <h2 class="h3 mb-3">Riepilogo</h2>
      <!-- Office -->
      <div class="bg-white p-4 mb-4" style="position: relative">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h3 class="h4 mb-3">{{ resource?.name || "" }}</h3>
          <button
            type="button"
            class="btn"
            @click="
              (e) => {
                modalRef.show();
              }
            "
          >
            Modifica
          </button>
        </div>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Descrizione</small>
        <p
          class="fw-bold mb-3"
          v-html="createDescriptionMarkup(resource?.description)"
        ></p>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Sede</small>
        <p class="fw-bold mb-3">{{ resource?.place || "" }}</p>
        <div class="border-top" style="height: 0.5rem">&nbsp;</div>
      </div>
      <!-- Dates -->
      <div class="bg-white p-4 mb-4" style="position: relative">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h3 class="h4 mb-3">Data e orario</h3>
          <button
            type="button"
            class="btn"
            @click="
              (e) => {
                store.commit('setIsRecapping', true);
                emit('goto', 1);
              }
            ">
            Modifica
          </button>
        </div>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Data</small>
        <p class="fw-bold mb-3">{{ store.state.slots[0].slotDate }}</p>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Ora</small>
        <p class="fw-bold mb-3">
          {{ store.state.slots[0].startTime }} -
          {{ store.state.slots[store.state.slots.length - 1].endTime }}
        </p>
        <div class="border-top" style="height: 0.5rem">&nbsp;</div>
      </div>

      <template v-if="isServiceActive && doesServiceDetermines">
      <!-- Motivo -->
      <div class="bg-white p-4 mb-4" style="position: relative">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h3 class="h4 mb-3">Motivo appuntamento</h3>
          <button
            type="button"
            class="btn"
            @click="
              (e) => {
                store.commit('setIsRecapping', true);
                emit('goto', 1);
              }">
            Modifica
          </button>
        </div>
        <div class="border-top mb-2" style="height: 0.5rem"></div>
        <small class="fw-normal">Motivo</small>
        <p class="fw-bold mb-3">
          {{
            services?.find(
              (service) => service.serviceId === store.state.serviceId
            )?.description || ""
          }}
        </p>
        <template v-if="store.state.serviceId && resource?.isInAllowedList">
          <div class="border-top mb-2" style="height: 0.5rem"></div>
          <small class="fw-normal">{{resource?.listType}}</small>
          <p class="fw-bold mb-3" v-for="(identify, index) in store?.state?.identifyValues" :key="index">{{index + 1}}) {{identify}}</p>
        </template>
      </div>
      </template>

      <!-- Details -->
      <div class="bg-white p-4 mb-4" style="position: relative">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h3 class="h4 mb-3">Dettagli appuntamento</h3>
          <button
            type="button"
            class="btn"
            @click="
              (e) => {
                store.commit('setIsRecapping', true);
                emit('goto', 2);
              }">
            Modifica
          </button>
        </div>

        <template v-if="store.state.serviceId && !doesServiceDetermines">
          <div class="border-top mb-2" style="height: 0.5rem"></div>
          <small class="fw-normal">Motivo</small>
          <p class="fw-bold mb-3">
            {{
              services?.find(
                (service) => service.serviceId === store.state.serviceId
              )?.description || ""
            }}
          </p>
        </template>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Dettagli</small>
        <p class="fw-bold mb-3">{{ store.state.detail }}</p>
      </div>

      <!-- Applicant -->
      <div class="bg-white p-4 mb-4" style="position: relative">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h3 class="h4 mb-3">Richiedente</h3>
          <button
            type="button"
            class="btn"
            @click="
              (e) => {
                store.commit('setIsRecapping', true);
                emit('goto', 3);
              }
            "
          >
            Modifica
          </button>
        </div>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Nome</small>
        <p class="fw-bold mb-3">{{ store.state.user.firstName }}</p>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Cognome</small>
        <p class="fw-bold mb-3">{{ store.state.user.lastName }}</p>
        <div class="border-top mb-2" style="height: 0.5rem">&nbsp;</div>
        <small class="fw-normal">Email</small>
        <p class="fw-bold mb-3">{{ store.state.user.email }}</p>
        <div class="border-top" style="height: 0.5rem">&nbsp;</div>
      </div>
      <div class="d-flex justify-content-between mt-4">
        <button
          type="button"
          class="btn btn-outline-primary"
          @click="
            (e) => {
              store.commit('setIsRecapping', true);
              emit('previous');
            }
          "
        >
          Indietro
        </button>
        <div></div>
        <button type="button" class="btn btn-outline-primary" @click="onNext">
          Avanti
        </button>
      </div>
    </div>
  </div>
  <div
    class="modal fade"
    tabindex="-1"
    role="dialog"
    id="editResourceModal"
    aria-labelledby="editResourceModalTitle"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title h5" id="editResourceModalTitle">
            Modifica Ufficio
          </h2>
        </div>
        <div class="modal-body">
          Stai per procedere alla modifica della risorsa. Cliccando su prosegui
          dovrai ripetere il processo di prenotazione. Vuoi continuare?
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-secondary btn-sm"
            data-bs-dismiss="modal"
            type="button"
          >
            Annulla
          </button>
          <button
            class="btn btn-primary btn-sm"
            data-bs-dismiss="modal"
            type="button"
            @click="resetReservation"
          >
            Procedi
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
